"use client";

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";

export default function PortalLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please fill in all fields.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setError("Invalid credentials. Please try again.");
    }, 1500);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10 relative overflow-hidden"
      style={{
        backgroundColor: "#f0f4f0",
        backgroundImage: `linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)`,
        backgroundSize: "40px 40px",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      }}
    >
      {/* Glow blobs background */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "5%", left: "-5%", width: "40vw", height: "40vw",
          background: "radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)",
          filter: "blur(50px)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: "5%", right: "-5%", width: "35vw", height: "35vw",
          background: "radial-gradient(circle, rgba(251,146,60,0.10) 0%, transparent 70%)",
          filter: "blur(50px)",
        }}
        aria-hidden="true"
      />

      {/* Back to Home */}
      <button
        onClick={() => navigate("/")}
        className="relative z-10 flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-green-700 transition-colors group mb-8 self-start ml-4 sm:ml-8 lg:ml-auto lg:mr-auto lg:w-full lg:max-w-5xl"
      >
        <span className="w-8 h-8 rounded-full border border-gray-300 bg-white flex items-center justify-center group-hover:border-green-600 group-hover:bg-green-50 transition-all shadow-sm">
          <ArrowLeft className="w-4 h-4" />
        </span>
        Back to Home
      </button>

      {/* ── CARD BOX ── */}
      <div className="relative z-10 w-full max-w-5xl rounded-3xl overflow-hidden shadow-2xl flex flex-col lg:flex-row"
        style={{ minHeight: "560px" }}
      >

        {/* ── LEFT — Form ── */}
        <div className="flex flex-col justify-center w-full lg:w-[45%] bg-white px-8 sm:px-12 py-12">

          {/* Logo */}
          <div className="mb-8 flex justify-center lg:justify-start">
            <img
              src="/Images/logo.png"
              alt="Fiieve"
              className="h-24 w-auto object-contain"
            />
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-1">
              Welcome back
            </h1>
            <p className="text-gray-400 text-sm">
              Sign in to your Fiieve client portal
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 text-sm placeholder-gray-300 outline-none focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20 transition-all"
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-xs font-bold uppercase tracking-widest text-gray-400">
                  Password
                </label>
                <button
                  type="button"
                  className="text-xs text-green-700 font-semibold hover:text-green-500 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3.5 pr-12 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 text-sm placeholder-gray-300 outline-none focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-4 py-3">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-green-700 hover:bg-green-600 active:scale-[0.98] text-white font-bold py-3.5 rounded-xl transition-all duration-300 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign In to Portal"
              )}
            </button>

          </form>

          {/* Footer note */}
          <p className="text-center text-xs text-gray-400 mt-8">
            Don't have access?{" "}
            <a href="/contact-us" className="text-green-700 font-semibold hover:text-green-500 transition-colors">
              Contact us
            </a>
          </p>
        </div>

        {/* ── RIGHT — Branded Panel ── */}
        <div
          className="hidden lg:flex flex-col justify-between w-[55%] relative overflow-hidden p-12"
          style={{ backgroundColor: "#00321a" }}
        >
          {/* Grid overlay */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(74,222,128,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.08) 1px, transparent 1px)`,
              backgroundSize: "40px 40px",
            }}
            aria-hidden="true"
          />

          {/* Glow blobs */}
          <div className="absolute pointer-events-none"
            style={{ top: "0%", left: "5%", width: "55%", height: "55%", background: "radial-gradient(circle, rgba(74,222,128,0.20) 0%, transparent 70%)", filter: "blur(50px)" }}
            aria-hidden="true"
          />
          <div className="absolute pointer-events-none"
            style={{ bottom: "5%", right: "0%", width: "40%", height: "40%", background: "radial-gradient(circle, rgba(251,146,60,0.18) 0%, transparent 70%)", filter: "blur(45px)" }}
            aria-hidden="true"
          />

          {/* Top: Headline */}
          <div className="relative z-10">
            <p className="text-green-400 text-xs font-bold uppercase tracking-widest mb-4">
              Client Portal
            </p>
            <h2 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight mb-4">
              Your projects,<br />
              <span className="text-green-400">all in one place.</span>
            </h2>
            <p className="text-gray-100 text-sm leading-relaxed max-w-xs">
              Track progress, review deliverables, and stay on top of every milestone — from one clean dashboard.
            </p>

            {/* Feature pills — single row with dividers */}
            <div className="flex items-center mt-6 bg-green-400/5 border border-green-400/20 rounded-2xl px-4 py-3 gap-0">
              {["Project Tracking", "File Sharing", "Live Updates", "Secure Access"].map((f, i, arr) => (
                <React.Fragment key={f}>
                  <span className="flex items-center gap-1.5 text-green-300 text-xs font-semibold whitespace-nowrap flex-1 justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" aria-hidden="true" />
                    {f}
                  </span>
                  {i < arr.length - 1 && (
                    <span className="w-px h-4 bg-green-400/20 flex-shrink-0" aria-hidden="true" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Bottom: Mock dashboard card */}
          <div className="relative z-10">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-white font-bold text-sm">Active Projects</p>
                  <p className="text-gray-100 text-xs mt-0.5">Updated just now</p>
                </div>
                <span className="flex items-center gap-1.5 bg-green-400/20 text-green-400 text-xs font-bold px-3 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Live
                </span>
              </div>
              <div className="space-y-3.5">
                {[
                  { name: "E-Commerce Platform", progress: 82, color: "bg-green-400" },
                  { name: "Mobile App — iOS",    progress: 55, color: "bg-orange-400" },
                  { name: "AI Dashboard",         progress: 30, color: "bg-green-300" },
                ].map((p) => (
                  <div key={p.name}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-gray-300 text-xs font-medium">{p.name}</p>
                      <p className="text-gray-100 text-xs">{p.progress}%</p>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5">
                      <div className={`${p.color} h-1.5 rounded-full`} style={{ width: `${p.progress}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-gray-100 text-xs mt-5">© 2025 Fiieve. All rights reserved.</p>
          </div>
        </div>

      </div>
    </div>
  );
}